import axios from "axios";
import moment from "moment";
import realSwal from "sweetalert";
import Vue from "vue";
import i18n from "../i18n";
import { track } from "swetrix";

async function swal() {
  const currentFocus = window.swalRestoreFocusTo || document.activeElement;
  const response = await realSwal.apply(this, arguments);
  if (currentFocus && typeof currentFocus.focus === "function") {
    currentFocus.focus();
  }
  return response;
}

export default {
  state: {
    programs: {},
    programsLoaded: false,
    programTypes: null,
    programTags: null,
    programTagLabel: i18n.t("message.tag"),
    programTagDescription: null,
    adminOrOwnerPrograms: null,
  },
  actions: {
    loadProgramSummaries({ commit, dispatch }) {
      return axios
        .get("/api/program-library/programs")
        .then((response) => {
          commit("storeProgramSummaries", response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadProgramDetail({ commit, dispatch }, { programOrId, explicitView }) {
      const programId = programOrId.id || programOrId;
      let uri = `/api/program-library/programs/${programId}`;
      if (explicitView) {
        // User is explictly taking an action to view details, therefore record it
        const affiliate = sessionStorage.getItem('affiliate');
        if (affiliate) {
          uri += `?affiliate=${affiliate}`;
        }
      }
      return axios
        .get(uri)
        .then((response) => {
          commit("storeProgramDetail", response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadAdminTeams({ commit, dispatch }, program) {
      if (!program.admin_teams) {
        axios
          .get(`/api/programs/${program.id}/admin-teams`)
          .then((response) => {
            commit("setAdminTeams", { program, teams: response.data });
          });
      }
    },
    loadTypes({ commit, dispatch }) {
      axios.get("/api/programs/program-types").then((response) => {
        commit("setProgramTypes", response.data);
      });
    },
    loadTags({ commit, dispatch }) {
      axios.get("/api/programs/tags").then((response) => {
        commit("setProgramTags", response.data.tags);
        commit("setProgramTagLabelAndDescription", {
          label: response.data.display_name,
          description: response.data.description,
        });
      });
    },
    loadSessionJoiners(
      { commit, dispatch, getters },
      { programId, sessionId }
    ) {
      const program = getters.program(programId);
      const session = program.sessions.find((s) => s.id === sessionId);
      if (session.joiners) {
        return;
      }

      axios
        .get(`/api/programs/${programId}/sessions/${sessionId}/joiners`)
        .then((response) => {
          commit("setSessionJoiners", {
            programId,
            sessionId,
            joiners: response.data,
          });
        });
    },
    async cancelRegistration({ commit, dispatch }, { session, program }) {
      const confirmed = await swal({
        title: i18n.t("message.unenrol"),
        text: i18n.t("message.confirm-unenrol", [
          session ? session.name : program.title,
        ]),
        buttons: true,
      });
      if (!confirmed) {
        return;
      }

      try {
        const body = {};
        if (session) {
          body.session_id = session.id;
        }
        await axios.post(
          `/api/programs/${program.id}/cancel_registration`,
          body
        );
      } catch (err) {
        swal(
          i18n.t("message.error"),
          err.response.data.error.description,
          "error"
        );
        return;
      }
      swal(
        i18n.t("message.success"),
        i18n.t("message.enrollment-cancelled", [
          session ? session.name : program.title,
        ]),
        "success"
      );
      if (session) {
        commit("setSessionRegistered", { session, registered: false });
      }
      if (program.learner_team) {
        commit("setLearnerTeam", { program, team: null });
      }
    },
    async archiveTeam({ commit, dispatch }, { program, team }) {
      const confirmed = await swal({
        title: i18n.t("message.remove-from-active"),
        text: i18n.t("message.confirm-remove-from-active-programs", [
          program.title,
        ]),
        buttons: true,
      });
      if (!confirmed) {
        return;
      }

      try {
        await axios.patch(`/api/programs/${program.id}/teams/${team.id}`, {
          is_archived: true,
        });
      } catch (err) {
        swal(
          i18n.t("message.error"),
          err.response.data.error.description,
          "error"
        );
        return;
      }
      commit("archiveLearnerTeam", { program, team });
    },
    async leaveTeam({ commit, dispatch }, { program, team }) {
      const confirmed = await swal({
        title: i18n.t("message.remove-permanently"),
        text: i18n.t("message.confirm-remove-permanently", [program.title]),
        buttons: true,
      });
      if (!confirmed) {
        return;
      }

      try {
        await axios.delete(`/api/programs/${program.id}/teams/${team.id}`);
      } catch (err) {
        swal(
          i18n.t("message.error"),
          err.response.data.error.description,
          "error"
        );
        return;
      }
      commit("setLearnerTeam", { program, team: null });

      // If in team via a session, remove from session
      if (team.session_id) {
        program.sessions
          .filter((s) => s.id == team.session_id)
          .forEach((s) => {
            commit("setSessionRegistered", { session: s, registered: false });
          });
      }
    },
    async registerForSession({ commit, dispatch }, { session, program }) {
      const confirmed = await confirmSessionEnrolment(session, program);
      if (!confirmed) {
        return;
      }

      try {
        await axios.post("/programs/api/enrol_in_session", {
          session_id: session.id,
          affiliate: sessionStorage.getItem('affiliate'),
        });

        track({
          ev: "program_enrol_in_session",
          meta: {
            programId: String(program.id),
            sessionId: String(session.id),
            userId: String(this.getters.currentUser.id),
            editionId: String(this.getters.currentEdition.id)
          }
        });
      } catch (err) {
        this.showError("message.unable-to-enroll", err);
        return;
      }

      // Unregister from other sessions
      program.sessions
        .filter((s) => s.registered)
        .forEach((s) => {
          commit("setSessionRegistered", { session: s, registered: false });
        });
      commit("setLearnerTeam", { program, team: null });
      commit("setSessionRegistered", { session, registered: true });

      swal(
        i18n.t("message.success"),
        i18n.t("message.enrolled-in-session", [session.name]),
        "success"
      );
    },
    async joinRunningSession({ commit, dispatch }, { session, program }) {
      const confirmed = await confirmSessionEnrolment(session, program);
      if (!confirmed) {
        return;
      }

      let response;
      try {
        response = await axios.post("/programs/api/join_running_session", {
          session_id: session.id,
          affiliate: sessionStorage.getItem('affiliate'),
        });

        track({
          ev: "program_join_running_session",
          meta: {
            programId: String(program.id),
            sessionId: String(session.id),
            userId: String(this.getters.currentUser.id),
            editionId: String(this.getters.currentEdition.id)
          }
        });
      } catch (err) {
        showError("message.unable-to-enroll", err);
        return;
      }

      // Unregister from other sessions
      program.sessions
        .filter((s) => s.registered || s.learner_team)
        .forEach((s) => {
          commit("setSessionRegistered", { session: s, registered: false });
        });

      commit("setSessionRegistered", { session, registered: true });
      commit("setLearnerTeam", { program, team: response.data });
    },
    async joinAlwaysOn({ commit, dispatch }, { alwaysOn, program }) {
      const name = alwaysOn.label || i18n.t("message.continuous-session");
      const confirmed = await confirmEnrolment(name, program);
      if (!confirmed) {
        return;
      }

      const url = `/programs/api/programs/${program.id}/auto_cohorts/${
        alwaysOn.id
      }/actions/enrol`;
      let response;
      try {
        response = await axios.post(url, {
          affiliate: sessionStorage.getItem('affiliate'),
        });
 
        track({
          ev: "program_join_always_on",
          meta: {
            programId: String(program.id),
            autoCohortId: String(alwaysOn.id),
            userId: String(this.getters.currentUser.id),
            editionId: String(this.getters.currentEdition.id)
          }
        });
      } catch (err) {
        showError("message.unable-to-enroll", err);
        return;
      }

      // Unregister from other sessions
      program.sessions
        .filter((s) => s.registered || s.learner_team)
        .forEach((s) => {
          commit("setSessionRegistered", { session: s, registered: false });
        });

      dispatch("loadProgramDetail", { programOrId: program });
    },
  },
  mutations: {
    storeProgramSummaries(state, programSummaries) {
      programSummaries.forEach((program) => {
        if (!state.programs[program.id]) {
          Vue.set(state.programs, program.id, program);
        }
      });
      state.programsLoaded = true;
    },
    storeProgramDetail(state, program) {
      Object.keys(program).forEach((key) => {
        Vue.set(state.programs[program.id], key, program[key]);
      });
    },
    setProgramTypes(state, types) {
      state.programTypes = types;
    },
    setProgramTags(state, tags) {
      state.programTags = tags;
    },
    setProgramTagLabelAndDescription(state, { label, description }) {
      state.programTagLabel = label;
      state.programTagDescription = description;
    },
    setAdminTeams(state, { program, teams }) {
      Vue.set(program, "admin_teams", teams);
    },
    setSessionJoiners(state, { programId, sessionId, joiners }) {
      Vue.set(
        Object.values(state.programs)
          .find((p) => p.id === programId)
          .sessions.find((s) => s.id === sessionId),
        "joiners",
        joiners
      );
    },
    setSessionRegistered(state, { session, registered }) {
      Vue.set(session, "registered", registered);
    },
    setLearnerTeam(state, { program, team }) {
      Vue.set(program, "learner_team", team);
    },
    archiveLearnerTeam(state, { program, team }) {
      if (program.learner_team) {
        Vue.set(program.learner_team, "is_archived", true);
        Vue.set(program.learner_team, "archived_date", new Date());
      }
    },
  },
  getters: {
    allPrograms(state) {
      return Object.values(state.programs);
    },
    program: (state) => (programOrId) => {
      const programId = programOrId.id || programOrId;
      return state.programs[programId];
    },
    programsLoaded(state) {
      return state.programsLoaded;
    },
    programTags(state) {
      return state.programTags;
    },
    programTypes(state) {
      return state.programTypes;
    },
    programsByType: (state) => (type) => {
      return Object.values(state.programs)
        .filter(available)
        .filter((p) => p.types.includes(type.id));
    },
    programsByTag: (state) => (tag) => {
      return Object.values(state.programs)
        .filter(available)
        .filter((p) => p.tags.includes(tag.id));
    },
    programTagLabel(state) {
      return state.programTagLabel;
    },
    programTagDescription(state) {
      return state.programTagDescription;
    },
    programTagBySlug: (state) => (slug) => {
      if (state.programTags) {
        return state.programTags.find((t) => t.slug == slug);
      }
    },
    programTypeBySlug: (state) => (slug) => {
      if (state.programTypes) {
        return state.programTypes.find((t) => t.slug == slug);
      }
    },
    activePrograms(state) {
      return Object.values(state.programs).filter(
        (p) =>
          p.learner_team &&
          !p.learner_team.is_archived &&
          (p.learner_team.end_date === null ||
            moment(p.learner_team.end_date).isAfter())
      );
    },
    registeredPrograms(state) {
      return Object.values(state.programs).filter((p) =>
        p.sessions.some(
          (s) =>
            s.registered &&
            (p.learner_team === null || p.learner_team.session_id !== s.id)
        )
      );
    },
    alwaysOnPrograms(state) {
      return Object.values(state.programs)
        .filter((p) => p.auto_cohorts.length)
        .sort((a, b) => {
          // If sort order specified, sort ascending by sort order.
          // Programs with specified order always appear before Programs
          // that do not.
          if (a.default_sort_order !== null && b.default_sort_order !== null) {
            return a.default_sort_order - b.default_sort_order;
          } else if (a.default_sort_order !== null) {
            return -1;
          } else if (b.default_sort_order !== null) {
            return 1;
          }
          // fall back to descending order of recency (i.e. highest value of auto_cohort record)
          const aMaxId = Math.max(...a.auto_cohorts.map((ac) => ac.id));
          const bMaxId = Math.max(...b.auto_cohorts.map((ac) => ac.id));
          return bMaxId - aMaxId;
        });
    },
    comingSoonPrograms(state) {
      // Returns subset of programs that are configured to be visible
      // but are not yet available to the user and do not fall into any other
      // category.
      return Object.values(state.programs).filter(
        (p) =>
          p.learner_team === null &&
          !p.auto_cohorts.length &&
          !p.sessions.length &&
          !p.has_more_teams &&
          p.always_show
      );
    },
    adminOrOwnerPrograms(state) {
      return Object.values(state.programs).filter((p) => p.has_more_teams);
    },
    previousPrograms(state) {
      return Object.values(state.programs).filter(
        (p) =>
          p.learner_team &&
          (p.learner_team.is_archived ||
            (p.learner_team.end_date !== null &&
              moment(p.learner_team.end_date).isBefore()))
      );
    },
  },
};

async function confirmSessionEnrolment(session, program) {
  let messageKey, icon;
  if (program.learner_team) {
    messageKey = "message.confirm-reenroll-with-dates";
    icon = "warning";
  } else {
    messageKey = "message.confirm-enroll-with-dates";
    icon = null;
  }
  let confirm = await swal({
    title: name,
    text: i18n.t(messageKey, {
      name: session.name,
      startDate: moment(session.start_date).format("MMMM D"),
      endDate: moment(session.end_date).format("MMMM D"),
    }),
    buttons: true,
    icon,
  });
  return confirm;
}

async function confirmEnrolment(name, program) {
  let messageKey, icon;
  if (program.learner_team) {
    messageKey = "message.confirm-reenroll";
    icon = "warning";
  } else {
    messageKey = "message.confirm-enroll";
    icon = null;
  }
  let confirm = await swal({
    title: name,
    text: i18n.t(messageKey, [name]),
    buttons: true,
    icon,
  });
  return confirm;
}

function showError(titleKey, err) {
  swal(i18n.t(titleKey), err.response.data.error.description, "error");
}

function available(program) {
  return !!(
    program.always_show ||
    program.learner_team ||
    program.auto_cohorts.length ||
    program.sessions.length
  );
}
